import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Table,
} from 'reactstrap';
import { useGetScanTunnelSessionQuery } from '../../../graphql/queries/getScanTunnelSession.generated';
import i18n from '@/utils/i18n';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { NetworkStatus } from 'apollo-client';
import { useRemoveScannedItemsFromSessionMutation } from '../../../graphql/mutations/removeScannedItemsFromSession.generated';
import { ContainerStationRow } from './components/ContainerStationRow';
import { Communicator } from '../../../tools/Communication';
import { getTimeFromDateStr } from '@/utils/time';
import { ContainersTable } from './components/ContainerTable';
import Checkbox from '../../../components/Checkbox';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../../components/Checkbox/pickpackCheckbox';
import { useClearContainerStationMutation } from '../../../graphql/mutations/clearContainerStation.generated';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';

type ContainerType = {
  id: number;
  name: string;
  imageUrl: string;
};

export const ScanTunnelSessionDetailPage = () => {
  const { scanTunnelSessionId } = useParams<{ scanTunnelSessionId: string }>();
  const history = useHistory();
  const {
    data,
    loading: loadingScanTunnelSession,
    refetch,
    error,
    networkStatus,
  } = useGetScanTunnelSessionQuery({
    variables: { id: scanTunnelSessionId },
    notifyOnNetworkStatusChange: true, // This ensures networkStatus updates trigger re-renders
    fetchPolicy: 'network-only',
  });
  const [clearContainerStation, { error: errorClearingContainerStation }] =
    useClearContainerStationMutation();
  const [isTransferring, setIsTransferring] = useState(false);
  const [clearContainerStationOnTransfer, setClearContainerStationOnTransfer] =
    useState(true);
  const [transferLog, setTransferLog] = useState<
    Array<{
      time: string;
      containerCount: number;
      containerStationId: string;
      containerStationName: string;
    }>
  >([]);

  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [containerTypeId, setContainerTypeId] = useState<number>(1);
  const [containerTypeSelected, setContainerTypeSelected] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const isInitialLoading =
    loadingScanTunnelSession && networkStatus !== NetworkStatus.refetch;
  const isRefetching = networkStatus === NetworkStatus.refetch;

  const [
    removeScannedItemsFromSessionMutation,
    {
      loading: removingScannedItemsFromSession,
      error: errorRemovingScannedItemsFromSession,
    },
  ] = useRemoveScannedItemsFromSessionMutation();

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  async function removeItemsFromSession(
    containerIds: string[],
    itemIds: string[],
  ) {
    await removeScannedItemsFromSessionMutation({
      variables: {
        sessionId: scanTunnelSessionId,
        containerIds: containerIds,
        itemIds: itemIds,
      },
    });
  }

  function getContainerTypeInfo() {
    const type = containerTypes.find((ct) => ct.id === (containerTypeId || 1));
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <p style={{ marginRight: '10px' }}>
          {i18n.t('ScanTunnelSession:changeContainersToType')}:
        </p>
        <img
          style={{ width: 40, height: 40, marginRight: '10px' }}
          src={type?.imageUrl || DEFAULT_CONTAINER_TYPE_ICON}
          alt=""
        />
        {type?.name}
      </div>
    );
  }

  async function changeContainerType() {
    if (session == null) {
      return;
    }
    await Communicator.PostData('/api/3/Containers/ChangeType', {
      containerCodes: session.containers
        .filter((c) => c.name.startsWith(session.id))
        .map((c) => `c${c.id}`),
      newTypeId: containerTypeId,
    }).then(() => refetch());
  }

  async function transferAllContainersToContainerStation(
    containerStationId: string,
  ) {
    console.log(
      'Transfer all containers to container station ' + containerStationId,
    );

    setIsTransferring(true);
    const containerIdsToTransfer = session?.containers.map((c) => c.id);
    if (!containerIdsToTransfer || containerIdsToTransfer.length === 0) {
      alert('No containers to transfer');
      setIsTransferring(false);
      return;
    }

    if (clearContainerStationOnTransfer) {
      try {
        await clearContainerStation({
          variables: {
            id: containerStationId,
          },
        });
      } catch (e) {
        alert('Error clearing container station: ' + e);
        return;
      }
    }

    try {
      const result = await Communicator.PostData(
        '/api/3/Container/BulkTransfer',
        {
          codes: [
            ...(session?.containers?.map((c) => c.id) || []),
            'HTTP://VYT.TO/CS/' + containerStationId,
          ],
        },
      );
      const parsedResult = await result.json();
      console.log('BulkTransfer result: ', parsedResult);
      if (parsedResult.result === 'Success') {
        setTransferLog([
          {
            time: new Date().toISOString(),
            containerCount: containerIdsToTransfer.length,
            containerStationId: containerStationId,
            containerStationName:
              session?.container_stations.find(
                (cs) => cs.id === containerStationId,
              )?.name || containerStationId,
          },
          ...transferLog,
        ]);
      } else {
        throw new Error('BulkTransfer failed: ' + parsedResult.result);
      }
    } catch (e) {
      alert('Error transferring containers to container station: ' + e);
    }
    try {
      await removeItemsFromSession(containerIdsToTransfer, [
        containerStationId,
      ]);
    } catch (e) {
      alert('Error removing containers from session: ' + e);
    }
    setIsTransferring(false);
  }

  const session = data?.getScanTunnelSession;
  useEffect(() => {
    if (session != null) {
      const intervalId = setInterval(async () => {
        if (!session?.is_active) return;
        await refetch();
      }, 3000);

      // Clean up the interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [session]);

  useEffect(() => {
    if (
      session?.containers &&
      session.containers.length > 0 &&
      !containerTypeSelected
    ) {
      const frequencyMap = session.containers.reduce(
        (acc, container) => {
          acc[container.type_id] = (acc[container.type_id] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const mostCommonTypeId = Object.keys(frequencyMap).reduce((a, b) =>
        frequencyMap[a] > frequencyMap[b] ? a : b,
      );
      setContainerTypeId(parseInt(mostCommonTypeId));
    }
  }, [session?.containers]);

  if (isInitialLoading || !session)
    return <div>{i18n.t('ScanTunnelSession:loading')}</div>;
  if (error || data?.getScanTunnelSession == null)
    return <div>{i18n.t('Common:error', { error: error?.message })}</div>;

  if (errorRemovingScannedItemsFromSession) {
    return (
      <div className="alert-danger">
        Error removing items from Scantunnel session:{' '}
        {errorRemovingScannedItemsFromSession.message}
      </div>
    );
  }

  if (errorClearingContainerStation) {
    return (
      <div className="alert-danger">
        Error clearing container station:{' '}
        {errorClearingContainerStation.message}
      </div>
    );
  }

  return (
    <ContentSection
      title={i18n.t('ScanTunnelSession:scanTunnelSession') + ': ' + session.id}
      collapsable={false}
    >
      <Button
        className="float-end"
        onClick={() =>
          history.push(Routes.scanTunnel(session.scan_tunnel_id.toString()))
        }
      >
        {i18n.t('ScanTunnelSession:backToScanTunnel')}
      </Button>
      <h4 className="mt-2 mb-2">
        {i18n.t('ScanTunnelSession:isActive') + ': ' + session.is_active}
      </h4>
      <h4 className="mt-2 mb-2">
        {i18n.t('ScanTunnelSession:activeUntil') +
          ': ' +
          (session.active_until || '-')}
      </h4>
      <div style={{ height: '30px' }}>{isRefetching ? 'Reloading...' : ''}</div>

      <h4 className="mt-4 mb-3">{i18n.t('ScanTunnelSession:containers')}</h4>
      <ContainersTable
        removeItemsFromSession={removeItemsFromSession}
        removingScannedItemsFromSession={removingScannedItemsFromSession}
        session={{
          ...session,
          containers: session.containers.filter(
            (c) => !c.name.startsWith(session.id),
          ),
        }}
      />
      <h4 className="mt-4 mb-3">
        {i18n.t('ScanTunnelSession:unencodedContainers')}
      </h4>
      <ContainersTable
        removeItemsFromSession={removeItemsFromSession}
        removingScannedItemsFromSession={removingScannedItemsFromSession}
        session={{
          ...session,
          containers: session.containers.filter((c) =>
            c.name.startsWith(session.id),
          ),
        }}
      />
      <FormGroup>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {getContainerTypeInfo()}
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret>Select Container Type</DropdownToggle>
            <DropdownMenu>
              {containerTypes.map((ct) => (
                <DropdownItem
                  key={ct.id}
                  onClick={() => {
                    setContainerTypeSelected(true);
                    setContainerTypeId(ct.id);
                  }}
                >
                  <img
                    style={{ width: 30, height: 30 }}
                    src={ct.imageUrl || DEFAULT_CONTAINER_TYPE_ICON}
                    alt={ct.name}
                  />
                  {ct.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <Button onClick={() => changeContainerType()}>
          {i18n.t('ScanTunnelSession:changeContainerType')}
        </Button>
      </FormGroup>
      <h4 className="mt-4 mb-3">
        {i18n.t('ScanTunnelSession:containerStations')}
      </h4>
      <div className="mb-2">
        <CheckBoxLabelContainer
          onClick={() =>
            setClearContainerStationOnTransfer(!clearContainerStationOnTransfer)
          }
        >
          <Checkbox
            /*
            // @ts-ignore */
            checked={clearContainerStationOnTransfer}
          />
          <CheckBoxLabel>
            {i18n.t('ScanTunnelSession:clearContainerStationOnTransfer')}
          </CheckBoxLabel>
        </CheckBoxLabelContainer>
      </div>
      <Table className="striped">
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            <th>{i18n.t('Common:type')}</th>
            <th>Transfer</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {session.container_stations.length === 0 && (
            <tr>
              <td colSpan={4}>
                {i18n.t('ScanTunnelSession:noContainerStations')}
              </td>
            </tr>
          )}
          {session.container_stations.map((cs) => (
            <ContainerStationRow
              key={cs.id}
              cs={cs}
              removeItemsFromSession={removeItemsFromSession}
              removingScannedItemsFromSession={removingScannedItemsFromSession}
              onTransferAllContainers={transferAllContainersToContainerStation}
              transferringContainers={isTransferring}
              scannedContainerCount={session.containers.length}
            />
          ))}
        </tbody>
      </Table>

      <br />
      <h4>Transfer log</h4>
      <Table>
        <thead>
          <tr>
            <th>Time</th>
            <th>Container count</th>
            <th>Container station</th>
          </tr>
        </thead>
        <tbody>
          {transferLog.map((log, i) => (
            <tr key={i}>
              <td>{getTimeFromDateStr(log.time)}</td>
              <td>{log.containerCount}</td>
              <td>
                <Link
                  to={'/container-station/' + log.containerStationId}
                  target="_blank"
                >
                  {log.containerStationName}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </ContentSection>
  );
};
